import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, BarChart2, Users, Map, Activity,
  CreditCard, Truck, Star, Weight, Calendar,
  Download, Printer, RefreshCw, Search, ChevronUp, ChevronDown
} from 'lucide-react';
import apiClient from '../services/api';

const REPORT_TYPES = [
  { key: 'consignment_details',  label: 'Consignment Details',  icon: FileText   },
  { key: 'consignment_summary',  label: 'Daily Summary',        icon: BarChart2  },
  { key: 'customer_analysis',    label: 'Customer Analysis',    icon: Users      },
  { key: 'route_analysis',       label: 'Route Analysis',       icon: Map        },
  { key: 'status_report',        label: 'Status Report',        icon: Activity   },
  { key: 'payment_status',       label: 'Payment Status',       icon: CreditCard },
  { key: 'delivery_report',      label: 'Delivery Report',      icon: Truck      },
  { key: 'performance_report',   label: 'Performance',          icon: Star       },
  { key: 'weight_analysis',      label: 'Weight Analysis',      icon: Weight     },
  { key: 'monthly_summary',      label: 'Monthly Summary',      icon: Calendar   },
];

const COLUMNS = {
  consignment_details: [
    { key:'ConsignmentNo',   label:'Consignment No',  width:140 },
    { key:'ConsignmentDate', label:'Date',             width:100, fmt:'date' },
    { key:'ConsignmentType', label:'Type',             width:100 },
    { key:'ShipperCode',     label:'Shipper Code',     width:100 },
    { key:'ShipperName',     label:'Shipper',          width:160 },
    { key:'ConsigneeCode',   label:'Consignee Code',   width:110 },
    { key:'ConsigneeName',   label:'Consignee',        width:160 },
    { key:'OriginCity',      label:'Origin',           width:110 },
    { key:'DestinationCity', label:'Destination',      width:120 },
    { key:'DriverName',      label:'Driver',           width:130, fmt:'driver' },
    { key:'Pieces',          label:'Pcs',              width:55,  align:'right' },
    { key:'Weight',          label:'Weight',           width:80,  align:'right', fmt:'num3' },
    { key:'InvoiceValue',    label:'Value',            width:90,  align:'right', fmt:'num2' },
    { key:'InvoiceNo',       label:'Invoice No',       width:120, fmt:'invoice' },
    { key:'Status',          label:'Status',           width:110, fmt:'status' },
    { key:'DeliveryDate',    label:'Delivery Date',    width:110, fmt:'date' },
    { key:'DaysInTransit',   label:'Days',             width:60,  align:'right', fmt:'days' },
    { key:'Remarks',         label:'Remarks',          width:160 },
  ],
  consignment_summary: [
    { key:'ConsignmentDate',  label:'Date',       width:110, fmt:'date' },
    { key:'TotalConsignments',label:'Total',      width:80,  align:'right' },
    { key:'TotalValue',       label:'Total Value',width:110, align:'right', fmt:'num2' },
    { key:'TotalWeight',      label:'Total Wt',   width:100, align:'right', fmt:'num3' },
    { key:'TotalPieces',      label:'Pieces',     width:75,  align:'right' },
    { key:'DeliveredCount',   label:'Delivered',  width:85,  align:'right', fmt:'green' },
    { key:'InTransitCount',   label:'In Transit', width:85,  align:'right', fmt:'orange' },
    { key:'PendingCount',     label:'Pending',    width:80,  align:'right', fmt:'red' },
    { key:'AvgDeliveryDays',  label:'Avg Days',   width:85,  align:'right', fmt:'num1' },
  ],
  customer_analysis: [
    { key:'CustomerCode',     label:'Code',       width:100 },
    { key:'CustomerName',     label:'Customer',   width:200 },
    { key:'TotalConsignments',label:'Total',      width:75,  align:'right' },
    { key:'TotalValue',       label:'Total Value',width:110, align:'right', fmt:'num2' },
    { key:'TotalWeight',      label:'Total Wt',   width:100, align:'right', fmt:'num3' },
    { key:'DeliveredCount',   label:'Delivered',  width:85,  align:'right', fmt:'green' },
    { key:'PendingCount',     label:'Pending',    width:80,  align:'right', fmt:'red' },
    { key:'FirstConsignment', label:'First Date', width:100, fmt:'date' },
    { key:'LastConsignment',  label:'Last Date',  width:100, fmt:'date' },
  ],
  route_analysis: [
    { key:'OriginCity',       label:'Origin',      width:140 },
    { key:'DestinationCity',  label:'Destination', width:140 },
    { key:'TotalConsignments',label:'Total',       width:75, align:'right' },
    { key:'TotalValue',       label:'Total Value', width:110,align:'right', fmt:'num2' },
    { key:'TotalWeight',      label:'Total Wt',    width:100,align:'right', fmt:'num3' },
    { key:'DeliveredCount',   label:'Delivered',   width:85, align:'right', fmt:'green' },
    { key:'AvgDeliveryDays',  label:'Avg Days',    width:85, align:'right', fmt:'num1' },
    { key:'DeliveryRate',     label:'Del. Rate %', width:95, align:'right', fmt:'pct' },
  ],
  status_report: [
    { key:'Status',           label:'Status',      width:140, fmt:'status' },
    { key:'TotalConsignments',label:'Count',       width:80,  align:'right' },
    { key:'Percentage',       label:'%',           width:75,  align:'right', fmt:'pct' },
    { key:'TotalValue',       label:'Total Value', width:120, align:'right', fmt:'num2' },
    { key:'TotalWeight',      label:'Total Wt',    width:100, align:'right', fmt:'num3' },
    { key:'EarliestDate',     label:'Earliest',    width:100, fmt:'date' },
    { key:'LatestDate',       label:'Latest',      width:100, fmt:'date' },
  ],
  payment_status: [
    { key:'ConsignmentNo',    label:'Consignment No', width:140 },
    { key:'ConsignmentDate',  label:'Date',           width:100, fmt:'date' },
    { key:'ShipperName',      label:'Shipper',        width:180 },
    { key:'InvoiceValue',     label:'Con. Value',     width:110, align:'right', fmt:'num2' },
    { key:'InvoicedAmount',   label:'Invoiced',       width:100, align:'right', fmt:'num2' },
    { key:'PaymentStatus',    label:'Pay. Status',    width:110, fmt:'payStatus' },
    { key:'InvoiceNo',        label:'Invoice No',     width:120 },
    { key:'InvoiceDate',      label:'Invoice Date',   width:110, fmt:'date' },
    { key:'ConsignmentStatus',label:'Con. Status',    width:110, fmt:'status' },
  ],
  delivery_report: [
    { key:'ConsignmentNo',   label:'Consignment No', width:140 },
    { key:'ConsignmentDate', label:'Date',           width:100, fmt:'date' },
    { key:'ConsigneeName',   label:'Consignee',      width:180 },
    { key:'DestinationCity', label:'Destination',    width:120 },
    { key:'DeliveryDate',    label:'Delivery Date',  width:110, fmt:'date' },
    { key:'DaysInTransit',   label:'Days',           width:60,  align:'right', fmt:'days' },
    { key:'DeliveryStatus',  label:'Status',         width:110, fmt:'deliveryStatus' },
    { key:'DeliveredBy',     label:'Delivered By',   width:130 },
    { key:'Weight',          label:'Weight',         width:80,  align:'right', fmt:'num3' },
    { key:'Pieces',          label:'Pcs',            width:55,  align:'right' },
    { key:'PODRemarks',      label:'POD Remarks',    width:180 },
  ],
  performance_report: [
    { key:'EmployeeName',     label:'Employee/Driver', width:180 },
    { key:'TotalConsignments',label:'Total',           width:75,  align:'right' },
    { key:'DeliveredCount',   label:'Delivered',       width:85,  align:'right', fmt:'green' },
    { key:'DeliveryRate',     label:'Del. Rate %',     width:95,  align:'right', fmt:'pct' },
    { key:'AvgDeliveryDays',  label:'Avg Days',        width:85,  align:'right', fmt:'num1' },
    { key:'TotalValue',       label:'Total Value',     width:120, align:'right', fmt:'num2' },
    { key:'TotalWeight',      label:'Total Wt',        width:100, align:'right', fmt:'num3' },
  ],
  weight_analysis: [
    { key:'WeightCategory',   label:'Weight Category', width:140 },
    { key:'TotalConsignments',label:'Count',           width:75,  align:'right' },
    { key:'TotalWeight',      label:'Total Wt',        width:100, align:'right', fmt:'num3' },
    { key:'AvgWeight',        label:'Avg Wt',          width:85,  align:'right', fmt:'num3' },
    { key:'TotalValue',       label:'Total Value',     width:120, align:'right', fmt:'num2' },
    { key:'AvgValue',         label:'Avg Value',       width:100, align:'right', fmt:'num2' },
    { key:'DeliveredCount',   label:'Delivered',       width:85,  align:'right', fmt:'green' },
    { key:'DeliveryRate',     label:'Del. Rate %',     width:95,  align:'right', fmt:'pct' },
  ],
  monthly_summary: [
    { key:'MonthYear',        label:'Month',           width:140 },
    { key:'TotalConsignments',label:'Total',           width:80,  align:'right' },
    { key:'TotalValue',       label:'Total Value',     width:120, align:'right', fmt:'num2' },
    { key:'TotalWeight',      label:'Total Wt',        width:100, align:'right', fmt:'num3' },
    { key:'DeliveredCount',   label:'Delivered',       width:85,  align:'right', fmt:'green' },
    { key:'PendingCount',     label:'Pending',         width:80,  align:'right', fmt:'red' },
    { key:'UniqueShippers',   label:'Shippers',        width:80,  align:'right' },
    { key:'UniqueConsignees', label:'Consignees',      width:90,  align:'right' },
    { key:'AvgDeliveryDays',  label:'Avg Days',        width:85,  align:'right', fmt:'num1' },
  ],
};

function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' });
}
function fmt2(v)   { return v != null && v !== '' ? parseFloat(v).toFixed(2) : '-'; }
function fmt3(v)   { return v != null && v !== '' ? parseFloat(v).toFixed(3) : '-'; }
function fmt1(v)   { return v != null && v !== '' ? parseFloat(v).toFixed(1) : '-'; }
function fmtPct(v) { return v != null && v !== '' ? parseFloat(v).toFixed(1) + '%' : '-'; }

const STATUS_COLORS = {
  'Delivered':   { color:'#15803d', fontWeight:'700' },
  'In Transit':  { color:'#d97706', fontWeight:'600' },
  'Overdue':     { color:'#dc2626', fontWeight:'700' },
  'Pending':     { color:'#7c3aed', fontWeight:'500' },
  'In Progress': { color:'#d97706', fontWeight:'500' },
  'Returned':    { color:'#6b7280', fontWeight:'500' },
  'Cancelled':   { color:'#ef4444', fontWeight:'500' },
};

function CellValue({ col, value }) {
  const v = value;
  switch (col.fmt) {
    case 'date':   return <span>{formatDate(v)}</span>;
    case 'num2':   return <span>{fmt2(v)}</span>;
    case 'num3':   return <span>{fmt3(v)}</span>;
    case 'num1':   return <span>{fmt1(v)}</span>;
    case 'pct':    return <span>{fmtPct(v)}</span>;
    case 'days': {
      const n = parseInt(v);
      const style = n > 14 ? { color:'#dc2626', fontWeight:'700' } : n > 7 ? { color:'#d97706', fontWeight:'600' } : {};
      return <span style={style}>{n >= 0 ? n : '-'}</span>;
    }
    case 'status':
    case 'deliveryStatus': {
      const s = String(v || '');
      return <span style={STATUS_COLORS[s] || {}}>{s || '-'}</span>;
    }
    case 'payStatus': {
      const s = String(v || '');
      const c = s === 'Invoiced' ? '#15803d' : s === 'Partial' ? '#d97706' : '#dc2626';
      return <span style={{ color: c, fontWeight: '600' }}>{s || '-'}</span>;
    }
    case 'invoice': {
      const s = String(v || '');
      if (!s || s === 'Not Invoiced') return <span style={{ color:'#dc2626', fontStyle:'italic' }}>Not Invoiced</span>;
      return <span style={{ color:'#15803d', fontWeight:'600' }}>{s}</span>;
    }
    case 'driver': {
      const s = String(v || '');
      if (!s || s === 'Not Assigned') return <span style={{ color:'#dc2626', fontStyle:'italic' }}>Not Assigned</span>;
      return <span>{s}</span>;
    }
    case 'green':  return <span style={{ color:'#15803d', fontWeight:'600' }}>{v ?? '-'}</span>;
    case 'orange': return <span style={{ color:'#d97706', fontWeight:'600' }}>{v ?? '-'}</span>;
    case 'red':    return <span style={{ color:'#dc2626', fontWeight:'600' }}>{v ?? '-'}</span>;
    default:       return <span>{v ?? '-'}</span>;
  }
}

const StatBadge = ({ label, value, color }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[0.7rem] text-gray-500 font-semibold uppercase">{label}</span>
    <span className="text-[1rem] font-extrabold font-mono" style={{ color }}>{value}</span>
  </div>
);

const NAVY = '#667eea';
const NAVY_DARK = '#764ba2';

const ConsignmentReports = () => {
  const today      = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';

  const [reportType, setReportType] = useState('consignment_details');
  const [startDate,  setStartDate]  = useState(monthStart);
  const [endDate,    setEndDate]    = useState(today);
  const [customerID, setCustomerID] = useState('');
  const [originID,   setOriginID]   = useState('');
  const [destID,     setDestID]     = useState('');
  const statusFilter = 'All Status';
  const [conType,    setConType]    = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [customers, setCustomers] = useState([]);
  const [cities,    setCities]    = useState([]);
  const [rows,      setRows]      = useState([]);
  const [summary,   setSummary]   = useState({});
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    apiClient.get('/ConsignmentReports.php?action=dropdowns')
      .then(res => {
        if (res.data.success) {
          setCustomers(res.data.data.customers || []);
          setCities(res.data.data.cities || []);
        }
      })
      .catch(err => console.error('Dropdown load error:', err));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true); setError(''); setHasLoaded(false); setSortKey('');
    const params = new URLSearchParams({ report_type: reportType, start_date: startDate, end_date: endDate, customer_id: customerID, origin_city_id: originID, dest_city_id: destID, status: statusFilter, consignment_type: conType });
    try {
      const res = await apiClient.get(`/ConsignmentReports.php?${params}`);
      if (res.data.success) { setRows(res.data.data.rows || []); setSummary(res.data.data.summary || {}); setHasLoaded(true); }
      else setError(res.data.message || 'Failed to load report');
    } catch (err) { setError(err.response?.data?.message || err.message || 'Error loading report'); }
    finally { setLoading(false); }
  }, [reportType, startDate, endDate, customerID, originID, destID, statusFilter, conType]);

  const displayRows = (() => {
    let r = rows;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter(row => Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q)));
    }
    if (sortKey) {
      r = [...r].sort((a, b) => {
        const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
        const an = parseFloat(av), bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  })();

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const exportCSV = () => {
    const cols   = COLUMNS[reportType] || [];
    const header = cols.map(c => c.label).join(',');
    const body   = displayRows.map(row => cols.map(c => { let v = row[c.key] ?? ''; if (c.fmt === 'date') v = formatDate(v); return `"${String(v).replace(/"/g,'""')}"`; }).join(',')).join('\n');
    const blob   = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement('a');
    a.href = url; a.download = `${reportType}_${startDate}_${endDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const cols = COLUMNS[reportType] || [];
    const rt   = REPORT_TYPES.find(r => r.key === reportType);
    const win  = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${rt?.label||'Report'}</title><style>body{font-family:Arial,sans-serif;font-size:11px;margin:16px;}h2{margin:0 0 4px;font-size:16px;}p.sub{margin:0 0 10px;color:#555;font-size:11px;}table{border-collapse:collapse;width:100%;}th{background:#667eea;color:#fff;padding:6px 8px;text-align:left;font-size:10px;}td{padding:5px 8px;border-bottom:1px solid #e5e7eb;}tr:nth-child(even)td{background:#f9fafb;}@media print{@page{margin:10mm;}}</style></head><body><h2>${rt?.label||'Consignment Report'}</h2><p class="sub">Period: ${startDate} to ${endDate} | Records: ${displayRows.length}</p><table><thead><tr>${cols.map(c=>`<th>${c.label}</th>`).join('')}</tr></thead><tbody>${displayRows.map(row=>`<tr>${cols.map(c=>{let v=row[c.key]??'';if(c.fmt==='date')v=formatDate(v);else if(c.fmt==='num2')v=fmt2(v);else if(c.fmt==='num3')v=fmt3(v);else if(c.fmt==='num1')v=fmt1(v);else if(c.fmt==='pct')v=fmtPct(v);const align=c.align==='right'?' style="text-align:right"':'';return`<td${align}>${v}</td>`;}).join('')}</tr>`).join('')}</tbody></table><script>window.onload=()=>{window.print();}</script></body></html>`);
    win.document.close();
  };

  const cols = COLUMNS[reportType] || [];

  const inpCls = 'w-full px-2.5 py-2 border border-gray-300 rounded-md text-[0.85rem] bg-white box-border outline-none focus:border-violet-400 transition-colors duration-150';
  const lblCls = 'block text-[0.78rem] font-semibold text-gray-700 mb-1';

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        .cr-row:hover td { background:#eff6ff !important; }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-7 py-6 flex-shrink-0 border-b-4"
        style={{ background:`linear-gradient(135deg,${NAVY} 0%,${NAVY_DARK} 100%)`, borderBottomColor:'#5a67d8', boxShadow:'0 4px 12px rgba(0,0,0,0.15)' }}>
        <div className="flex items-center gap-5">
          <div className="w-[52px] h-[52px] bg-white/25 rounded-[14px] flex items-center justify-center flex-shrink-0">
            <BarChart2 size={26} color="#fff" />
          </div>
          <div>
            <div className="text-[1.7rem] font-extrabold" style={{ color:'#fff', textShadow:'0 2px 6px rgba(0,0,0,0.5)' }}>Consignment Reports</div>
            <div className="text-[0.85rem] mt-0.5" style={{ color:'rgba(255,255,255,0.8)' }}>
              {REPORT_TYPES.find(r => r.key === reportType)?.label}
              {hasLoaded && ` · ${displayRows.length} records`}
            </div>
          </div>
        </div>
      </div>

      {/* Report type tabs */}
      <div className="flex overflow-x-auto gap-1 px-4 py-3 bg-gray-100 border-b border-gray-200 flex-shrink-0">
        {REPORT_TYPES.map(rt => {
          const active = rt.key === reportType;
          const Icon   = rt.icon;
          return (
            <button key={rt.key} onClick={() => { setReportType(rt.key); setHasLoaded(false); }}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg border-0 cursor-pointer whitespace-nowrap text-[0.82rem] transition-all duration-150 flex-shrink-0 ${active ? 'font-bold text-white shadow-md' : 'font-medium text-gray-700 bg-white shadow-sm hover:brightness-95'}`}
              style={active ? { background:`linear-gradient(135deg,${NAVY},${NAVY_DARK})`, boxShadow:'0 2px 8px rgba(102,126,234,0.4)' } : {}}>
              <Icon size={13} />{rt.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="px-5 py-3.5 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="grid gap-2.5 items-end" style={{ gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))' }}>
          <div><label className={lblCls}>From Date</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inpCls} /></div>
          <div><label className={lblCls}>To Date</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inpCls} /></div>
          <div><label className={lblCls}>Customer</label>
            <select value={customerID} onChange={e => setCustomerID(e.target.value)} className={inpCls + ' cursor-pointer'}>
              <option value="">All Customers</option>
              {customers.map(c => <option key={c.customerid} value={c.customerid}>{c.customercode} – {c.customername}</option>)}
            </select>
          </div>
          <div><label className={lblCls}>Origin City</label>
            <select value={originID} onChange={e => setOriginID(e.target.value)} className={inpCls + ' cursor-pointer'}>
              <option value="">All Cities</option>
              {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
            </select>
          </div>
          <div><label className={lblCls}>Destination City</label>
            <select value={destID} onChange={e => setDestID(e.target.value)} className={inpCls + ' cursor-pointer'}>
              <option value="">All Cities</option>
              {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
            </select>
          </div>
          <div><label className={lblCls}>Con. Type</label>
            <select value={conType} onChange={e => setConType(e.target.value)} className={inpCls + ' cursor-pointer'}>
              <option value="">All Types</option>
              <option value="LT">Land Transport</option>
              <option value="D">Domestic</option>
              <option value="A">Air</option>
              <option value="S">Sea</option>
            </select>
          </div>
          <div>
            <label className={lblCls}>&nbsp;</label>
            <button onClick={loadReport} disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 px-3.5 py-2.5 border-0 rounded-md text-white font-bold text-[0.9rem] cursor-pointer disabled:cursor-not-allowed transition-all duration-150"
              style={{ background: loading ? '#9ca3af' : `linear-gradient(135deg,${NAVY},${NAVY_DARK})` }}>
              {loading
                ? <><span style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }} />Loading...</>
                : <><RefreshCw size={14} />Load Report</>}
            </button>
          </div>
        </div>
      </div>

      {/* Summary bar + actions */}
      {hasLoaded && (
        <div className="flex items-center gap-5 flex-wrap px-5 py-2.5 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <StatBadge label="Records" value={displayRows.length} color={NAVY} />
          {summary.total_value  > 0 && <StatBadge label="Total Value"  value={parseFloat(summary.total_value).toFixed(2)}           color="#10b981" />}
          {summary.total_weight > 0 && <StatBadge label="Total Weight" value={parseFloat(summary.total_weight).toFixed(3) + ' kg'}   color="#f59e0b" />}
          {summary.delivered_count > 0 && <StatBadge label="Delivered" value={summary.delivered_count}                               color="#15803d" />}
          <div className="ml-auto flex gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 rounded-lg bg-white">
              <Search size={14} className="text-gray-400" />
              <input type="text" placeholder="Search…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="border-0 outline-none w-[140px] text-[0.85rem] bg-transparent" />
            </div>
            <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-700 text-[0.82rem] font-semibold cursor-pointer hover:bg-gray-50 transition-colors duration-100">
              <Download size={15} /> CSV
            </button>
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg bg-white text-gray-700 text-[0.82rem] font-semibold cursor-pointer hover:bg-gray-50 transition-colors duration-100">
              <Printer size={15} /> Print
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mx-5 mt-3 px-3.5 py-2.5 rounded-lg bg-red-50 border border-red-300 text-red-700 text-[0.9rem] flex-shrink-0">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Table area */}
      <div className="flex-1 overflow-auto">
        {!hasLoaded && !loading && (
          <div className="text-center py-16 text-gray-400">
            <BarChart2 size={48} className="mx-auto mb-3 opacity-40" />
            <p className="text-[1rem] m-0">Select filters and click <strong>Load Report</strong></p>
          </div>
        )}

        {loading && (
          <div className="text-center py-16" style={{ color: NAVY }}>
            <span style={{ width:32, height:32, border:'3px solid rgba(102,126,234,0.3)', borderTopColor: NAVY, borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block', marginBottom:12 }} />
            <p className="m-0">Loading report…</p>
          </div>
        )}

        {hasLoaded && !loading && (
          displayRows.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FileText size={40} className="mx-auto mb-2.5 opacity-40" />
              <p className="m-0">No data found for the selected filters.</p>
            </div>
          ) : (
            <table className="w-full border-collapse text-[0.82rem]" style={{ minWidth:800 }}>
              <thead>
                <tr className="bg-gray-100 sticky top-0 z-10">
                  {cols.map(c => (
                    <th key={c.key} onClick={() => handleSort(c.key)}
                      className="py-2.5 px-2.5 border-b-2 border-r border-gray-200 font-bold text-[0.78rem] text-gray-700 cursor-pointer whitespace-nowrap select-none"
                      style={{ textAlign: c.align || 'left', minWidth: c.width }}>
                      <span className="inline-flex items-center gap-0.5">
                        {c.label}
                        {sortKey === c.key
                          ? sortDir === 'asc' ? <ChevronUp size={12} color={NAVY} /> : <ChevronDown size={12} color={NAVY} />
                          : <ChevronUp size={12} color="#d1d5db" />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayRows.map((row, i) => {
                  const isInvoiced = row.IsInvoiced === 1 || row.IsInvoiced === '1';
                  const baseBg = isInvoiced ? '#f0fdf4' : i % 2 === 0 ? '#fff' : '#f9fafb';
                  return (
                    <tr key={i} className="cr-row" style={{ background: baseBg }}>
                      {cols.map(c => (
                        <td key={c.key}
                          className="py-1.5 px-2.5 border-b border-r border-gray-100"
                          style={{ textAlign: c.align || 'left', whiteSpace: c.width > 140 ? 'normal' : 'nowrap', fontFamily: c.align === 'right' ? '"Courier New",monospace' : 'inherit' }}>
                          <CellValue col={c} value={row[c.key]} row={row} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              {displayRows.length > 1 && (
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    {cols.map((c, ci) => {
                      if (ci === 0) return <td key={c.key} className="px-2.5 py-2 border-t-2 border-gray-300 text-[0.8rem] text-gray-800">TOTAL ({displayRows.length})</td>;
                      const numFmts = ['num2','num3','num1','green','orange','red'];
                      if (numFmts.includes(c.fmt) || c.align === 'right') {
                        const sum = displayRows.reduce((s, r) => s + (parseFloat(r[c.key]) || 0), 0);
                        const val = c.fmt === 'num3' ? fmt3(sum) : c.fmt === 'num1' ? fmt1(sum) : c.fmt === 'pct' ? '' : fmt2(sum);
                        return <td key={c.key} className="px-2.5 py-2 border-t-2 border-gray-300 text-[0.8rem] text-gray-800 text-right font-mono">{val}</td>;
                      }
                      return <td key={c.key} className="px-2.5 py-2 border-t-2 border-gray-300" />;
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          )
        )}
      </div>
    </div>
  );
};

export default ConsignmentReports;